/*
  omes — OMES Bundles in the cart.

  A set (« ensemble »), a box or a quantity offer reaches the cart as ordinary lines, each
  tagged with a hidden `_omes_bundle` property, and a Shopify discount code kept by the
  marketplace app brings those lines down to the bundle's price. This file keeps the
  cart's codes in step with its lines. After every add, quantity change and removal it
  works out which OMES codes the cart has earned, and posts them together with every code
  that is not OMES's.

  `_omes_bundle`, identical on every line of one bundle:
    q|<product id>|<quantity>:<code>|…   a quantity offer, one pair per tier
    s|<set product id>|<pieces>|<code>   a set
    b|<box product id>|<units>|<code>    a box (« box au choix »)
  The ids are Shopify's numeric product ids, as /cart.js reports `product_id`.

  Rules:
    - quantity offer: the code of the highest tier at or below the product's units in
      the cart — every unit of that product counts, as Shopify's minimum does — and no
      code below the first tier;
    - set: the code while every piece is in the cart, i.e. the distinct products among
      its s| lines number <pieces>;
    - box: the code while its b| lines hold at least <units> units, whichever pieces they
      are: the customer chose them, and the same one may be there twice;
    - a code that does not start with OMES- is never touched;
    - /cart/update.js `discount` replaces the whole list, so it is posted only when the
      list changes.

  Contract: omes_multi_vendor docs/backlog_omes_bundles.md, « The cart contract ».
  Used by assets/omes-product.js (the pickers), snippets/omes-prestep-checkout.liquid
  (before checkout), and the two cart UIs that change lines without the theme's cart
  event: sections/main-cart.liquid and sections/omes-checkout.liquid.
*/
(function () {
  'use strict';
  if (window.OmesBundles) return;

  var PREFIX = 'OMES-';
  var PROPERTY = '_omes_bundle';

  function route(name, fallback) {
    return ((window.routes && window.routes[name]) || fallback) + '.js';
  }

  function parse(value) {
    var parts = String(value || '').split('|');
    if (parts[0] === 'q' && parts.length >= 3 && parts[1]) {
      var tiers = [];
      for (var i = 2; i < parts.length; i++) {
        var colon = parts[i].indexOf(':');
        var quantity = parseInt(parts[i].slice(0, colon), 10);
        var code = parts[i].slice(colon + 1);
        if (colon > 0 && quantity > 1 && code) tiers.push({ quantity: quantity, code: code });
      }
      return tiers.length ? { type: 'q', key: parts[1], tiers: tiers } : null;
    }
    if (parts[0] === 's' && parts.length === 4 && parts[1]) {
      var pieces = parseInt(parts[2], 10);
      return pieces > 0 && parts[3] ? { type: 's', key: parts[1], pieces: pieces, code: parts[3] } : null;
    }
    if (parts[0] === 'b' && parts.length === 4 && parts[1]) {
      var units = parseInt(parts[2], 10);
      return units > 1 && parts[3] ? { type: 'b', key: parts[1], units: units, code: parts[3] } : null;
    }
    return null;
  }

  function isOmesCode(code) {
    return String(code || '').toUpperCase().indexOf(PREFIX) === 0;
  }

  /* The OMES codes a cart's lines have earned, in no particular order. */
  function earnedCodes(cart) {
    var items = (cart && cart.items) || [];
    var unitsByProduct = {};
    var offers = {};
    var sets = {};
    var boxes = {};

    items.forEach(function (item) {
      var productKey = String(item.product_id);
      unitsByProduct[productKey] = (unitsByProduct[productKey] || 0) + (item.quantity || 0);
      var bundle = parse(item.properties && item.properties[PROPERTY]);
      if (!bundle) return;
      if (bundle.type === 'q') {
        offers[bundle.key] = bundle.tiers;
      } else if (bundle.type === 'b') {
        var box = boxes[bundle.key] || (boxes[bundle.key] = { units: bundle.units, code: bundle.code, held: 0 });
        box.held += item.quantity || 0;
      } else {
        var set = sets[bundle.key] || (sets[bundle.key] = { pieces: bundle.pieces, code: bundle.code, products: {} });
        set.products[productKey] = true;
      }
    });

    var codes = [];
    Object.keys(offers).forEach(function (key) {
      var units = unitsByProduct[key] || 0;
      var best = null;
      offers[key].forEach(function (tier) {
        if (tier.quantity <= units && (!best || tier.quantity > best.quantity)) best = tier;
      });
      if (best) codes.push(best.code);
    });
    Object.keys(sets).forEach(function (key) {
      if (Object.keys(sets[key].products).length >= sets[key].pieces) codes.push(sets[key].code);
    });
    Object.keys(boxes).forEach(function (key) {
      if (boxes[key].held >= boxes[key].units) codes.push(boxes[key].code);
    });
    return codes;
  }

  /* The cart's codes now, and the list it should carry: every code not OMES's, then the earned ones. */
  function codesFor(cart) {
    var current = ((cart && cart.discount_codes) || []).map(function (d) { return d.code; });
    var wanted = [];
    function add(code) {
      var upper = String(code).toUpperCase();
      if (!wanted.some(function (w) { return String(w).toUpperCase() === upper; })) wanted.push(code);
    }
    current.forEach(function (code) { if (!isOmesCode(code)) add(code); });
    earnedCodes(cart).forEach(add);
    return { current: current, wanted: wanted };
  }

  function sameCodes(a, b) {
    function key(list) {
      return list.map(function (c) { return String(c).toUpperCase(); }).sort().join(',');
    }
    return a.length === b.length && key(a) === key(b);
  }

  /*
    What the cart UI on this page re-renders: the drawer's lines, the cart page's, and the
    discount form's code pills. The pills matter: that form sends the codes it shows plus
    the customer's, so a pill missing an OMES code would drop it.
  */
  function renderTargets() {
    var targets = [];
    ['cart-drawer-items', 'cart-items', 'cart-discount'].forEach(function (tag) {
      var el = document.querySelector(tag);
      if (!el || typeof el.getSectionsToRender !== 'function') return;
      try {
        el.getSectionsToRender().forEach(function (s) {
          if (!s || !s.section) return;
          var same = targets.some(function (t) { return t.id === s.id && t.selector === s.selector; });
          if (!same) targets.push(s);
        });
      } catch (e) {
        /* a cart UI whose sections are not on this page */
      }
    });
    return targets;
  }

  function render(targets, sections) {
    if (!sections) return;
    targets.forEach(function (s) {
      var html = sections[s.section];
      var host = document.getElementById(s.id);
      if (!html || !host) return;
      var target = (s.selector && host.querySelector(s.selector)) || host;
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector || '.shopify-section');
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  }

  function readCart() {
    return fetch(route('cart_url', '/cart'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  var queue = Promise.resolve();
  var busy = 0;

  /*
    Brings the cart's OMES codes in line with its lines, and re-renders the cart drawer
    when they change. `cart` is an up-to-date /cart.js state when the caller has one.
    `sections` names more sections to render with the update, for a cart UI that draws
    itself (the cart page). Runs one at a time; a call made while another runs reads the
    cart afresh, since the one before it may have changed the codes.
    Resolves with `{ cart, changed }`: the cart as it stands and whether its codes were
    changed, in which case `cart.sections` holds the rendered sections. Changing a code
    gives the lines it touches new keys, so a UI that addresses lines by key must draw
    them again. Resolves with null when the update failed (logged, never thrown).
  */
  function reconcile(cart, sections) {
    var useGiven = busy === 0 && !!cart && !!cart.items && !!cart.discount_codes;
    busy++;
    var run = function () {
      return (useGiven ? Promise.resolve(cart) : readCart()).then(function (state) {
        var codes = codesFor(state);
        if (sameCodes(codes.current, codes.wanted)) return { cart: state, changed: false };
        var targets = renderTargets();
        var body = { discount: codes.wanted.join(',') };
        var wanted = [];
        targets.map(function (t) { return t.section; }).concat(sections || []).forEach(function (id) {
          if (id && wanted.indexOf(id) === -1) wanted.push(id);
        });
        if (wanted.length) {
          body.sections = wanted;
          body.sections_url = window.location.pathname;
        }
        return fetch(route('cart_update_url', '/cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body)
        })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(function (next) {
            render(targets, next.sections);
            return { cart: next, changed: true };
          });
      });
    };
    var settled = function (result) {
      busy--;
      return result;
    };
    queue = queue.then(run, run).then(settled, function (err) {
      busy--;
      console.error('[omes-bundles] cart codes not updated:', err);
      return null;
    });
    return queue;
  }

  function listen() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        // A product form's event carries what it added, not the cart: reconcile reads it then.
        reconcile(event && event.cartData);
      });
    }
    // The cart page may open on codes the drawer's own discount form dropped.
    if (document.querySelector('cart-items')) reconcile();
  }

  window.OmesBundles = {
    PROPERTY: PROPERTY,
    parse: parse,
    earnedCodes: earnedCodes,
    codesFor: codesFor,
    reconcile: reconcile
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', listen);
  else listen();
})();
